import { Avatar, Kicker, StatusPill } from "@/components/iris";
import { Sheet, SheetClose, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { useAuthStore } from "@/features/auth/store";
import {
  useCreateUser,
  useDeleteUser,
  useResetPassword,
  useUnlockLogin,
  useUpdateUser,
  useUser,
} from "@/hooks/use-users";
import type { Env } from "@/lib/types";
import { Pencil, X } from "lucide-react";
import { useEffect, useState } from "react";
import { AvatarUpload } from "./user-avatar-upload";

interface UserPanelProps {
  userId: string | null;
  open: boolean;
  onClose: () => void;
  createMode: boolean;
  env?: Env | undefined;
}

type Mode = "view" | "edit" | "create";

const TYPE_VARIANT: Record<string, "idle" | "ok" | "warn"> = {
  user: "idle",
  admin: "ok",
  superadmin: "warn",
};

export function UserPanel({
  userId,
  open,
  onClose,
  createMode: isCreateMode,
  env,
}: UserPanelProps) {
  const { data: user } = useUser(userId, env);
  const updateUser = useUpdateUser(env);
  const deleteUser = useDeleteUser(env);
  const createUser = useCreateUser(env);
  const resetPassword = useResetPassword(env);
  const unlockLogin = useUnlockLogin(env);
  const currentUser = useAuthStore((s) => s.user);

  const [mode, setMode] = useState<Mode>("view");
  const [form, setForm] = useState({
    username: "",
    email: "",
    user_type: "user" as "user" | "admin",
  });
  const [createForm, setCreateForm] = useState({
    username: "",
    email: "",
    password: "",
    confirmPassword: "",
    user_type: "user" as "user" | "admin",
  });
  const [pwForm, setPwForm] = useState({ newPassword: "", confirmPassword: "" });
  const [showPwReset, setShowPwReset] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [unlockMessage, setUnlockMessage] = useState<string | null>(null);

  // Reset state when panel opens/closes or user changes
  // biome-ignore lint/correctness/useExhaustiveDependencies: userId and open are props, not outer-scope mutations
  useEffect(() => {
    if (isCreateMode) {
      setMode("create");
      setCreateForm({
        username: "",
        email: "",
        password: "",
        confirmPassword: "",
        user_type: "user",
      });
    } else {
      setMode("view");
    }
    setShowPwReset(false);
    setShowDeleteConfirm(false);
    setError(null);
    setUnlockMessage(null);
  }, [userId, isCreateMode, open]);

  // Populate edit form when switching to edit mode
  useEffect(() => {
    if (mode === "edit" && user) {
      setForm({
        username: user.username ?? "",
        email: user.email ?? "",
        user_type: user.user_type === "superadmin" ? "admin" : user.user_type,
      });
    }
  }, [mode, user]);

  const isSuperadmin = user?.user_type === "superadmin";
  const isSelf = user && currentUser && String(user.email) === String(currentUser.email);

  const handleSave = async () => {
    if (!user) return;
    setError(null);
    try {
      await updateUser.mutateAsync({ id: user.id, data: form });
      setMode("view");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Update failed");
    }
  };

  const handleCreate = async () => {
    setError(null);
    if (createForm.password !== createForm.confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    if (createForm.password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }
    try {
      await createUser.mutateAsync({
        username: createForm.username,
        email: createForm.email,
        password: createForm.password,
        user_type: createForm.user_type,
      });
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Create failed");
    }
  };

  const handleResetPassword = async () => {
    if (!user) return;
    setError(null);
    if (pwForm.newPassword !== pwForm.confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    if (pwForm.newPassword.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }
    try {
      await resetPassword.mutateAsync({ id: user.id, newPassword: pwForm.newPassword });
      setShowPwReset(false);
      setPwForm({ newPassword: "", confirmPassword: "" });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Password reset failed");
    }
  };

  const handleUnlock = async () => {
    if (!user?.email) return;
    setError(null);
    setUnlockMessage(null);
    try {
      const result = await unlockLogin.mutateAsync(user.email);
      setUnlockMessage(
        result.data.was_locked
          ? "Account was locked — cleared."
          : "Account was not locked (no-op).",
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : "Unlock failed");
    }
  };

  const handleDelete = async () => {
    if (!user) return;
    setError(null);
    try {
      await deleteUser.mutateAsync(user.id);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Delete failed");
    }
  };

  const handleToggleStatus = async () => {
    if (!user) return;
    setError(null);
    const newStatus = user.status === "active" ? "inactive" : "active";
    try {
      await updateUser.mutateAsync({ id: user.id, data: { status: newStatus } });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Status change failed");
    }
  };

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <SheetTitle>{mode === "create" ? "Create User" : "User Details"}</SheetTitle>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {mode === "view" && !isSuperadmin && (
              <button type="button" className="iris-btn" onClick={() => setMode("edit")}>
                <Pencil size={13} /> Edit
              </button>
            )}
            <SheetClose asChild>
              <button type="button" className="iris-btn" style={{ padding: 8 }}>
                <X size={14} />
              </button>
            </SheetClose>
          </div>
        </div>

        {error && (
          <div
            style={{
              marginTop: 12,
              borderRadius: "var(--iris-r-md)",
              background: "rgba(255, 77, 210, 0.08)",
              border: "1px solid rgba(255, 77, 210, 0.2)",
              padding: "8px 12px",
              fontSize: 12,
              color: "var(--iris-neon-pink)",
            }}
          >
            {error}
          </div>
        )}

        {/* -- CREATE MODE -- */}
        {mode === "create" && (
          <div style={{ marginTop: 24, display: "flex", flexDirection: "column", gap: 16 }}>
            <Field label="Full Name">
              <input
                className="iris-input"
                style={{ width: "100%" }}
                value={createForm.username}
                onChange={(e) => setCreateForm((f) => ({ ...f, username: e.target.value }))}
              />
            </Field>
            <Field label="Email">
              <input
                type="email"
                className="iris-input"
                style={{ width: "100%" }}
                value={createForm.email}
                onChange={(e) => setCreateForm((f) => ({ ...f, email: e.target.value }))}
              />
            </Field>
            <Field label="User Type">
              <select
                className="iris-input"
                style={{ width: "100%", cursor: "pointer" }}
                value={createForm.user_type}
                onChange={(e) =>
                  setCreateForm((f) => ({
                    ...f,
                    user_type: e.target.value as "user" | "admin",
                  }))
                }
              >
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </select>
            </Field>
            <Field label="Password">
              <input
                type="password"
                className="iris-input"
                style={{ width: "100%" }}
                value={createForm.password}
                onChange={(e) => setCreateForm((f) => ({ ...f, password: e.target.value }))}
              />
            </Field>
            <Field label="Confirm Password">
              <input
                type="password"
                className="iris-input"
                style={{ width: "100%" }}
                value={createForm.confirmPassword}
                onChange={(e) => setCreateForm((f) => ({ ...f, confirmPassword: e.target.value }))}
              />
            </Field>
            <div style={{ display: "flex", gap: 8, paddingTop: 8 }}>
              <button
                type="button"
                className="iris-btn iris-btn--primary"
                onClick={handleCreate}
                disabled={createUser.isPending}
              >
                {createUser.isPending ? "Creating..." : "Create"}
              </button>
              <button type="button" className="iris-btn" onClick={onClose}>
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* -- VIEW MODE -- */}
        {mode === "view" && user && (
          <div style={{ marginTop: 24, display: "flex", flexDirection: "column", gap: 24 }}>
            {/* Header */}
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
              {user.profile_image_url ? (
                <img
                  src={user.profile_image_url}
                  alt=""
                  style={{ width: 56, height: 56, borderRadius: 999, objectFit: "cover" }}
                />
              ) : (
                <Avatar name={user.username || "?"} size={56} />
              )}
              <span style={{ fontSize: 16, fontWeight: 600, color: "var(--iris-fg-1)" }}>
                {user.username}
              </span>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <StatusPill variant={TYPE_VARIANT[user.user_type] ?? "idle"}>
                  {user.user_type === "superadmin"
                    ? "Superadmin"
                    : user.user_type === "admin"
                      ? "Admin"
                      : "User"}
                </StatusPill>
                <span style={{ color: "var(--iris-fg-4)" }}>&middot;</span>
                <span style={{ fontSize: 13, color: "var(--iris-fg-3)" }}>
                  {user.status === "active" ? "Active" : "Inactive"}
                </span>
              </div>
            </div>

            {/* Fields */}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 12,
                borderTop: "1px solid var(--iris-line-1)",
                paddingTop: 16,
              }}
            >
              <DetailRow label="Full Name" value={user.username} />
              <DetailRow label="Email" value={user.email} />
              <DetailRow
                label="User Type"
                value={
                  user.user_type === "superadmin"
                    ? "Superadmin"
                    : user.user_type === "admin"
                      ? "Admin"
                      : "User"
                }
              />
              <DetailRow label="Status" value={user.status === "active" ? "Active" : "Inactive"} />
              <DetailRow
                label="Created"
                value={
                  user.user_created_at
                    ? new Date(user.user_created_at).toLocaleDateString("en-US", {
                        month: "long",
                        day: "numeric",
                        year: "numeric",
                      })
                    : "\u2014"
                }
              />
              <DetailRow
                label="Last Login"
                value={
                  user.last_login_at
                    ? new Date(user.last_login_at).toLocaleDateString("en-US", {
                        month: "long",
                        day: "numeric",
                        year: "numeric",
                        hour: "numeric",
                        minute: "2-digit",
                      })
                    : "Never"
                }
              />
              <DetailRow
                label="Last Updated"
                value={
                  user.system_update_time
                    ? new Date(user.system_update_time).toLocaleDateString("en-US", {
                        month: "long",
                        day: "numeric",
                        year: "numeric",
                      })
                    : "\u2014"
                }
              />
            </div>

            {/* Actions */}
            {!isSuperadmin && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 12,
                  borderTop: "1px solid var(--iris-line-1)",
                  paddingTop: 16,
                }}
              >
                {/* Unlock Account (clears per-email login lockout — IR-645 Slice B) */}
                <button
                  type="button"
                  className="iris-btn"
                  style={{ width: "100%" }}
                  onClick={handleUnlock}
                  disabled={unlockLogin.isPending}
                >
                  {unlockLogin.isPending ? "Unlocking..." : "Unlock Account"}
                </button>
                {unlockMessage && (
                  <div
                    style={{
                      borderRadius: "var(--iris-r-md)",
                      background: "rgba(132, 255, 132, 0.08)",
                      border: "1px solid rgba(132, 255, 132, 0.2)",
                      padding: "8px 12px",
                      fontSize: 12,
                      color: "var(--iris-neon-green, #84ff84)",
                    }}
                  >
                    {unlockMessage}
                  </div>
                )}

                {/* Reset Password */}
                {!showPwReset ? (
                  <button
                    type="button"
                    className="iris-btn"
                    style={{ width: "100%" }}
                    onClick={() => setShowPwReset(true)}
                  >
                    Reset Password
                  </button>
                ) : (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 8,
                      borderRadius: "var(--iris-r-md)",
                      border: "1px solid var(--iris-line-1)",
                      padding: 12,
                    }}
                  >
                    <input
                      type="password"
                      placeholder="New password"
                      className="iris-input"
                      style={{ width: "100%" }}
                      value={pwForm.newPassword}
                      onChange={(e) => setPwForm((f) => ({ ...f, newPassword: e.target.value }))}
                    />
                    <input
                      type="password"
                      placeholder="Confirm password"
                      className="iris-input"
                      style={{ width: "100%" }}
                      value={pwForm.confirmPassword}
                      onChange={(e) =>
                        setPwForm((f) => ({ ...f, confirmPassword: e.target.value }))
                      }
                    />
                    <div style={{ display: "flex", gap: 8 }}>
                      <button
                        type="button"
                        className="iris-btn iris-btn--primary"
                        onClick={handleResetPassword}
                        disabled={resetPassword.isPending}
                      >
                        {resetPassword.isPending ? "Saving..." : "Save"}
                      </button>
                      <button
                        type="button"
                        className="iris-btn"
                        onClick={() => {
                          setShowPwReset(false);
                          setPwForm({ newPassword: "", confirmPassword: "" });
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                <div style={{ display: "flex", gap: 8 }}>
                  <button
                    type="button"
                    className="iris-btn"
                    style={{ flex: 1 }}
                    onClick={handleToggleStatus}
                  >
                    {user.status === "active" ? "Deactivate" : "Activate"}
                  </button>
                  {!isSelf &&
                    (!showDeleteConfirm ? (
                      <button
                        type="button"
                        className="iris-btn iris-btn--neon"
                        style={{ flex: 1 }}
                        onClick={() => setShowDeleteConfirm(true)}
                      >
                        Delete
                      </button>
                    ) : (
                      <div style={{ display: "flex", flex: 1, gap: 4 }}>
                        <button
                          type="button"
                          className="iris-btn iris-btn--neon"
                          style={{ flex: 1 }}
                          onClick={handleDelete}
                          disabled={deleteUser.isPending}
                        >
                          Confirm
                        </button>
                        <button
                          type="button"
                          className="iris-btn"
                          onClick={() => setShowDeleteConfirm(false)}
                        >
                          No
                        </button>
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* -- EDIT MODE -- */}
        {mode === "edit" && user && (
          <div style={{ marginTop: 24, display: "flex", flexDirection: "column", gap: 16 }}>
            <AvatarUpload
              userId={user.id}
              currentUrl={user.profile_image_url}
              username={user.username ?? ""}
              env={env}
            />
            <Field label="Full Name">
              <input
                className="iris-input"
                style={{ width: "100%" }}
                value={form.username}
                onChange={(e) => setForm((f) => ({ ...f, username: e.target.value }))}
              />
            </Field>
            <Field label="Email">
              <input
                type="email"
                className="iris-input"
                style={{ width: "100%" }}
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              />
            </Field>
            <Field label="User Type">
              <select
                className="iris-input"
                style={{ width: "100%", cursor: "pointer" }}
                value={form.user_type}
                onChange={(e) =>
                  setForm((f) => ({ ...f, user_type: e.target.value as "user" | "admin" }))
                }
              >
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </select>
            </Field>
            <div style={{ display: "flex", gap: 8, paddingTop: 8 }}>
              <button
                type="button"
                className="iris-btn iris-btn--primary"
                onClick={handleSave}
                disabled={updateUser.isPending}
              >
                {updateUser.isPending ? "Saving..." : "Save"}
              </button>
              <button type="button" className="iris-btn" onClick={() => setMode("view")}>
                Cancel
              </button>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      {/* biome-ignore lint/a11y/noLabelWithoutControl: children is always an input/select/textarea */}
      <label>
        <Kicker variant="muted" style={{ marginBottom: 6 }}>
          {label}
        </Kicker>
        {children}
      </label>
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
      <span style={{ color: "var(--iris-fg-4)" }}>{label}</span>
      <span style={{ color: "var(--iris-fg-2)" }}>{value || "\u2014"}</span>
    </div>
  );
}
